import { apiService } from "api/apiService";
import type {
  TeacherPortfolioTaskResource,
  TeacherPortfolioTaskShallowResource,
  FilterResource,
  TaskProgressResource,
  PortfolioTaskQuestionResource,
  PortfolioTaskQuestionUpdateRequest,
  PortfolioAssignmentResource,
} from "api/generated";
import type { AssessmentFormData } from "portfolio/types";
import {
  assessmentFormDataToDto,
  teacherScheduledTaskFromDto,
  teacherPortfolioTaskFromDto,
  taskProgressFromDto,
  taskQuestionFromDto,
} from "portfolio/api/mappers";
import type {
  TeacherScheduledTask,
  TeacherPortfolioTask,
  TaskProgress,
  TaskDto,
  TaskFormData,
  TaskQuestion,
} from "portfolio/types";
import type { PaginatedResponse, PaginatedResult } from "api/types";
import type { Filter } from "shared/types";
import { mapPaginatedResponse } from "api/mappers/pagination.mapper";
import { mapFilters } from "shared/mappers";

export const fetchScheduledTasks = async (
  params: Record<string, string> = {},
): Promise<PaginatedResult<TeacherScheduledTask>> => {
  const { data } = await apiService.get<
    PaginatedResponse<TeacherPortfolioTaskShallowResource>
  >("api/portfolio/tasks", { params });

  return mapPaginatedResponse(data, teacherScheduledTaskFromDto);
};

export const fetchTasksFilters = async (): Promise<Filter[]> => {
  const {
    data: { data: filters },
  } = await apiService.get<{ data: FilterResource[] }>(
    "api/portfolio/tasks/filters",
  );

  return mapFilters(filters);
};

export const fetchScheduledTaskById = async (
  taskId: number,
): Promise<TeacherPortfolioTask> => {
  const {
    data: { data: task },
  } = await apiService.get<{ data: TeacherPortfolioTaskResource }>(
    `api/portfolio/tasks/${taskId}`,
  );

  return teacherPortfolioTaskFromDto(task);
};

export const fetchTaskProgress = async (
  taskId: number,
  params: Record<string, string> = {},
): Promise<TaskProgress> => {
  const { data: progress } = await apiService.get<TaskProgressResource>(
    `api/portfolio/tasks/progress/${taskId}`,
    { params },
  );

  return taskProgressFromDto(progress);
};

export const fetchTaskProgressFilters = async (
  taskId: number,
): Promise<Filter[]> => {
  const {
    data: { data: filters },
  } = await apiService.get<{ data: FilterResource[] }>(
    `api/portfolio/tasks/progress/${taskId}/filters`,
  );

  return mapFilters(filters);
};

export const createTask = async (
  dto: TaskDto,
): Promise<TeacherPortfolioTask> => {
  const {
    data: { data: task },
  } = await apiService.post<{ data: TeacherPortfolioTaskResource }>(
    "api/portfolio/tasks",
    dto,
  );

  return teacherPortfolioTaskFromDto(task);
};

export const createTaskDraft = async (
  dto: TaskDto,
): Promise<TeacherPortfolioTask> => {
  const {
    data: { data: task },
  } = await apiService.post<{ data: TeacherPortfolioTaskResource }>(
    "api/portfolio/tasks/draft",
    dto,
  );

  return teacherPortfolioTaskFromDto(task);
};

export const updateTask = async (
  taskId: number,
  dto: TaskDto,
): Promise<{ task: TeacherPortfolioTask; preservedStudentNames: string[] }> => {
  const {
    data: { data: task, preserved_student_names },
  } = await apiService.put<{
    data: TeacherPortfolioTaskResource;
    preserved_student_names?: string[];
  }>(`api/portfolio/tasks/${taskId}`, dto);

  return {
    task: teacherPortfolioTaskFromDto(task),
    preservedStudentNames: preserved_student_names ?? [],
  };
};

export const updateTaskDraft = async (
  taskId: number,
  dto: TaskDto,
): Promise<TeacherPortfolioTask> => {
  const {
    data: { data: task },
  } = await apiService.put<{ data: TeacherPortfolioTaskResource }>(
    `api/portfolio/tasks/${taskId}/draft`,
    dto,
  );

  return teacherPortfolioTaskFromDto(task);
};

export const deleteTask = async (taskId: number): Promise<void> => {
  await apiService.delete(`api/portfolio/tasks/${taskId}`);
};

export const createTaskAttachments = async (
  taskId: number,
  files: File[],
  onUploadProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<TeacherPortfolioTask> => {
  const formData = new FormData();

  files.forEach((file) => {
    formData.append("attachments[]", file);
  });

  const {
    data: { data: task },
  } = await apiService.post<{ data: TeacherPortfolioTaskResource }>(
    `api/portfolio/tasks/${taskId}/attachments`,
    formData,
    {
      signal,
      onUploadProgress: (event) => {
        if (!onUploadProgress || !event.total) {
          return;
        }
        onUploadProgress(Math.round((event.loaded / event.total) * 100));
      },
    },
  );

  return teacherPortfolioTaskFromDto(task);
};

export const deleteTaskAttachment = async (
  taskId: number,
  attachmentId: string,
): Promise<void> => {
  await apiService.delete(
    `api/portfolio/tasks/${taskId}/attachments/${attachmentId}`,
  );
};

export const uploadTaskBanner = async (
  taskId: number,
  file: File,
): Promise<{ message: string }> => {
  const formData = new FormData();
  formData.append("banner_image", file);

  const { data } = await apiService.post<{ message: string }>(
    `api/portfolio/tasks/${taskId}/banner`,
    formData,
  );

  return data;
};

export const deleteTaskBanner = async (
  taskId: number,
): Promise<{ message: string }> => {
  const { data } = await apiService.delete<{ message: string }>(
    `api/portfolio/tasks/${taskId}/banner`,
  );

  return data;
};

// --- Task Questions ---

const TASK_TYPE_PREFIX: Record<TaskFormData["type"], string> = {
  task: "tasks",
  template_task: "template-tasks",
  public_task: "public-tasks",
};

const getQuestionBaseUrl = (
  taskType: TaskFormData["type"],
  taskId: number,
): string => `api/portfolio/${TASK_TYPE_PREFIX[taskType]}/${taskId}/questions`;

export const createTaskQuestion = async (
  taskType: TaskFormData["type"],
  taskId: number,
  payload: PortfolioTaskQuestionUpdateRequest,
): Promise<TaskQuestion> => {
  const { data } = await apiService.post<PortfolioTaskQuestionResource>(
    getQuestionBaseUrl(taskType, taskId),
    payload,
  );

  return taskQuestionFromDto(data);
};

export const updateTaskQuestion = async (
  taskType: TaskFormData["type"],
  taskId: number,
  questionId: number,
  payload: PortfolioTaskQuestionUpdateRequest,
): Promise<TaskQuestion> => {
  const { data } = await apiService.put<PortfolioTaskQuestionResource>(
    `${getQuestionBaseUrl(taskType, taskId)}/${questionId}`,
    payload,
  );

  return taskQuestionFromDto(data);
};

export const deleteTaskQuestion = async (
  taskType: TaskFormData["type"],
  taskId: number,
  questionId: number,
): Promise<void> => {
  await apiService.delete(
    `${getQuestionBaseUrl(taskType, taskId)}/${questionId}`,
  );
};

export const reorderTaskQuestion = async (
  taskType: TaskFormData["type"],
  taskId: number,
  questionId: number,
  order: number,
): Promise<TaskQuestion> => {
  const { data } = await apiService.post<PortfolioTaskQuestionResource>(
    `${getQuestionBaseUrl(taskType, taskId)}/${questionId}/move`,
    { order },
  );

  return taskQuestionFromDto(data);
};

// --- Task Owners ---

export const addScheduledTaskOwners = async (
  taskId: number,
  ownerIds: number[],
): Promise<TeacherPortfolioTask> => {
  const {
    data: { data: task },
  } = await apiService.post<{ data: TeacherPortfolioTaskResource }>(
    `api/portfolio/tasks/${taskId}/owners/add`,
    { owners: ownerIds },
  );

  return teacherPortfolioTaskFromDto(task);
};

export const removeScheduledTaskOwner = async (
  taskId: number,
  ownerId: number,
): Promise<TeacherPortfolioTask> => {
  const {
    data: { data: task },
  } = await apiService.delete<{ data: TeacherPortfolioTaskResource }>(
    `api/portfolio/tasks/${taskId}/owners/${ownerId}/remove`,
  );

  return teacherPortfolioTaskFromDto(task);
};

export const addMentorsAsScheduledTaskOwners = async (
  taskId: number,
): Promise<TeacherPortfolioTask> => {
  const {
    data: { data: task },
  } = await apiService.post<{ data: TeacherPortfolioTaskResource }>(
    `api/portfolio/tasks/${taskId}/owners/add-mentors`,
  );

  return teacherPortfolioTaskFromDto(task);
};

// --- Task Conversions ---

export const convertLibraryTaskToScheduledTask = async (
  templateTaskId: number,
): Promise<TeacherPortfolioTask> => {
  const {
    data: { data: task },
  } = await apiService.post<{ data: TeacherPortfolioTaskResource }>(
    `api/portfolio/tasks/from-library/${templateTaskId}`,
  );

  return teacherPortfolioTaskFromDto(task);
};

export const convertPublicTaskToScheduledTask = async (
  publicTaskId: number,
): Promise<TeacherPortfolioTask> => {
  const {
    data: { data: task },
  } = await apiService.post<{ data: TeacherPortfolioTaskResource }>(
    `api/portfolio/public-tasks/${publicTaskId}/create-task`,
  );

  return teacherPortfolioTaskFromDto(task);
};

export const storeTaskAsTemplate = async (
  taskId: number,
  payload: {
    subjects?: string[];
    education_levels?: string[];
  },
): Promise<void> => {
  await apiService.post(
    `api/portfolio/tasks/${taskId}/storeAsTemplate`,
    payload,
  );
};

export const sendTaskReminder = async (
  taskId: number,
): Promise<{ message: string }> => {
  const { data } = await apiService.post<{ message: string }>(
    `api/portfolio/tasks/${taskId}/send-reminder`,
  );

  return data;
};

export const createAssessment = async (
  assignmentId: number,
  formData: AssessmentFormData,
): Promise<void> => {
  const dto = assessmentFormDataToDto(formData);

  await apiService.post<{ data: PortfolioAssignmentResource }>(
    `api/portfolio/assignments/${assignmentId}/assessments`,
    dto,
  );
};

export const updateAssessment = async (
  assignmentId: number,
  assessmentId: number,
  formData: AssessmentFormData,
): Promise<void> => {
  const dto = assessmentFormDataToDto(formData);

  await apiService.put<{ data: PortfolioAssignmentResource }>(
    `api/portfolio/assignments/${assignmentId}/assessments/${assessmentId}`,
    dto,
  );
};

// TODO: Update endpoint once backend delete route is implemented
export const deleteAssessment = async (
  assignmentId: number,
  assessmentId: number,
): Promise<void> => {
  await apiService.delete(
    `api/portfolio/assignments/${assignmentId}/assessments/${assessmentId}`,
  );
};
